/**
 * La peche, cote client: lancer la ligne, attendre, ferrer a temps.
 *
 * POURQUOI CE MODULE EXISTE. Le meme petit jeu se joue depuis la rive et depuis
 * la barque, et son etat (`peche`, `aleaPeche`) trainait parmi les quarante
 * champs de la boucle de jeu, lu a cinq endroits differents. Ici il est PRIVE:
 * on ne peut plus le remettre a zero par megarde depuis le rendu ou le reseau,
 * on ne peut que lancer, ferrer, et faire passer le temps.
 *
 * Les regles restent dans `shared/sim/vehicules.ts` (`commencerPeche`, `ferrer`,
 * `majPeche`): ce module orchestre, parle au joueur et journalise.
 */
import type { Joueur } from '../shared/types/player.ts'
import type { ITerrain, Vec2 } from '../shared/types/world.ts'

import type { Alea } from '../shared/sim/alea.ts'
import { commencerPeche, ferrer, majPeche, pecheFinie, type EtatPeche } from '../shared/sim/vehicules.ts'
import type { ItemId } from '../shared/types/items.ts'
import { objet } from '../shared/data/objets.ts'
import { audio } from './audio/audio.ts'
import { journal } from './log/journal.ts'

export interface RappelsPeche {
  message(texte: string, duree: number, genre: 'info' | 'succes' | 'erreur'): void
  /**
   * Une prise entre dans le sac — et c'est le SERVEUR qui l'y met.
   *
   * Le poisson qu'on voit sortir de l'eau est celui que `ferrer` vient de
   * tirer, ici, pour que le geste reponde au doigt. Ce qu'on garde, non: sans
   * cela il suffisait de s'ecrire un poisson sans jamais lancer la ligne.
   */
  gagnerPrise(item: ItemId): void
  /** Persiste le joueur: la peche depense de l'energie. */
  sauverJoueur(): void
}

export class Peche {
  private readonly terrain: ITerrain
  private readonly alea: Alea
  private readonly rappels: RappelsPeche
  private etat: EtatPeche | null = null

  constructor(terrain: ITerrain, alea: Alea, rappels: RappelsPeche) {
    this.terrain = terrain
    this.alea = alea
    this.rappels = rappels
  }

  /** Une ligne est-elle a l'eau? */
  get enCours(): boolean {
    return this.etat !== null
  }

  /** L'infobulle a montrer quand une ligne est a l'eau, sinon null. */
  indice(): string | null {
    if (!this.etat) return null
    return this.etat.phase === 'touche' ? 'Ferre !' : 'Ça pêche…'
  }

  /**
   * Lance la ligne a cet endroit de l'eau.
   *
   * `ancre = true` sans condition: depuis la rive on ne derive pas, et depuis la
   * barque l'ancre est verifiee avant d'arriver ici.
   */
  lancer(flotteur: Vec2, joueur: Joueur): void {
    joueur.chemin = []
    joueur.direction = null
    // On se tourne vers son flotteur. Depuis la rive la ligne part maintenant a
    // neuf metres: sans ca, le bonhomme pechait de dos, canne pointee ailleurs.
    // A bord, c'est la barque qui commande l'orientation: on n'y touche pas.
    if (joueur.vehicule === null) {
      const dx = flotteur.x - joueur.pos.x
      const dz = flotteur.z - joueur.pos.z
      if (dx * dx + dz * dz > 1e-4) joueur.rot = Math.atan2(dx, dz)
    }
    this.etat = commencerPeche(flotteur, this.terrain, this.alea, true)
    audio.clic()
    this.rappels.message('🎣 Ligne lancée… attends que ça morde.', 2600, 'info')
    journal.input('peche', `lancer (${flotteur.x.toFixed(1)}, ${flotteur.z.toFixed(1)})`)
  }

  /**
   * Ferre la prise. Renvoie vrai si le tap a bien servi a ca, pour que l'appelant
   * n'aille pas ensuite interpreter le meme tap comme un ordre de deplacement.
   */
  ferrerMaintenant(_joueur: Joueur): boolean {
    if (!this.etat) return false
    const r = ferrer(this.etat)
    if (r.ok && r.prise) {
      /*
       * LA PRISE VIENT DU SERVEUR, comme tout ce qui entre dans le sac.
       *
       * Le poisson qu'on VOIT est celui que `ferrer` a tire ici — c'est de
       * l'animation, et elle doit repondre au doigt. Ce qu'on GARDE, non: le
       * sac est tenu par le serveur (server/inventaire.ts), sans quoi il
       * suffisait de s'ecrire un poisson sans jamais lancer la ligne.
       */
      this.rappels.gagnerPrise(r.prise)
      audio.ramasser()
      this.rappels.message(`🎣 ${objet(r.prise).nom} !`, 2600, 'succes')
      journal.input('peche', `prise: ${r.prise}`)
      this.rappels.sauverJoueur()
    } else {
      this.rappels.message(r.raison ?? 'Raté.', 2200, 'info')
      audio.erreurInterface()
      journal.input('peche', `rate: ${r.raison ?? 'inconnu'}`)
    }
    this.etat = null
    return true
  }

  /** Fait passer le temps sur la ligne a l'eau. Un pas de la boucle de jeu. */
  maj(dt: number): void {
    if (!this.etat) return
    const e = majPeche(this.etat, dt)
    if (e.mord) {
      audio.ramasser()
      this.rappels.message('🎣 Ça mord ! Touche vite !', 1200, 'succes')
      journal.debug('peche', 'touche')
    }
    if (e.file) {
      this.rappels.message('Le poisson a filé…', 2000, 'info')
      journal.input('peche', 'rate: trop tard')
    }
    if (pecheFinie(this.etat)) this.etat = null
  }
}
